import React, { useState } from 'react';
import { actions, formatColumnTitle, MOBILE_ROLES_MODEL } from '../Model/AllowedModules';
import { useTranslation } from 'react-i18next';
import CustomTh from '../../../../../components/table/CustomTh';
import CustomTd from '../../../../../components/table/CustomTd';
import { CustomCheckbox } from '../../../../../components/CustomCheckbox';
import CustomTooltip from '../../../../../components/CustomTooltip';
import {
    IconCheckbox,
    IconCheckboxChecked,
    IconCheckboxUnchecked,
} from '../../../../../components/buttons/IconButtons';
import { countSelectedPermissions, handleSelectAllPermissions, isAllPermissionsSelected } from './CommonsPermissions';
import { ACTION_MODEL } from '../../../../../Helpers/ActionModel';

/**
 * @Author: Susana Garcia Rodriguez
 * @JobTitle: Frontend - IBL
 * @Email: sgarcia@ibl.mx
 */

const MobileRolesApp = ({ mobile, selectedPermissions, setSelectedPermissions, readOnly }) => {
    const { t } = useTranslation();
    const [selectedInnerView, setSelectedInnerView] = useState(MOBILE_ROLES_MODEL[0]?.id);

    const tableId = 'tbl_mra_table';

    const handleStepSelectedInnerView = (tab) => {
        setSelectedInnerView(tab);
    };
    const selectedRole = MOBILE_ROLES_MODEL.find((item) => item.id === selectedInnerView);
    // Tabs are listed alphabetically by their translated name
    const roles = [...MOBILE_ROLES_MODEL].sort((a, b) => t(a.translation).localeCompare(t(b.translation)));
    /**
     * Permissions one tab holds: the ones of the module and the ones of its
     * children, each returned by the backend under its own module id.
     *
     * @param {Object} role - Entry of MOBILE_ROLES_MODEL.
     * @returns {Array} Permissions of that tab.
     */
    const getRolePermissions = (role) =>
        (mobile || []).filter(
            (permission) =>
                permission.moduleId === role?.id ||
                role?.children?.some((child) => child.id === permission.moduleId),
        );

    const permissions = getRolePermissions(selectedRole);
    // Mobile permissions name the action first (READ_EVENT) while the web ones name it
    // last (WEB_EVENT_READ), so the action is read from the first token. Without it no
    // permission matched the lowercase action list and every one of them became a
    // column of its own, headed by the permission name instead of the shared action.
    const getAction = (permissionName = '') => {
        const action = permissionName.trim().split('_')[0]?.toLowerCase();

        return action === ACTION_MODEL.VIEW.toLowerCase() ? ACTION_MODEL.READ.toLowerCase() : action;
    };

    const standardColumns = actions.filter((action) =>
        permissions.some((permission) => getAction(permission.name) === action),
    );

    const customColumns = [
        ...new Set(
            permissions
                .filter((permission) => !actions.includes(getAction(permission.name)))
                .map((permission) => permission.name),
        ),
    ];

    const columns = [...standardColumns, ...customColumns];
    const thead = [
        <CustomTh tableId={tableId} key={'module'}>
            {''}
        </CustomTh>,

        ...columns.map((column) => (
            <CustomTh tableId={tableId} key={column}>
                <CustomTooltip text={formatColumnTitle(t(column))}>
                    <div className='text-center text-capitalize'>{formatColumnTitle(t(column))}</div>
                </CustomTooltip>
            </CustomTh>
        )),
    ];
    /**
     * Resolves the permission a cell renders, so the select all checkbox and the row
     * share a single rule and cannot disagree.
     *
     * @param {Array} childPermissions - Permissions belonging to the row module.
     * @param {string} column - Column key: an action or a full permission name.
     * @returns {Object|undefined} The permission shown in that cell, if any.
     */
    const resolveCellPermission = (childPermissions, column) =>
        childPermissions.find((permission) => {
            if (actions.includes(column)) {
                return getAction(permission.name) === column;
            }

            return permission.name === column;
        });

    const handleCheckboxChange = (permissionId, checked) => {
        setSelectedPermissions((prev = []) => {
            if (checked) {
                const exists = prev.some((item) => item.id === permissionId);
                if (exists) return prev;
                return [...prev, { id: permissionId }];
            }
            return prev.filter((item) => item.id !== permissionId);
        });
    };

    const tbody = {
        body: (selectedRole?.children?.length ? selectedRole.children : [selectedRole]).map((child) => {
            const childPermissions = permissions.filter((permission) => permission.moduleId === child.id);

            return (
                <>
                    <CustomTd tableId={tableId}>
                        <p className='fs-12px font-color-text-950 ibl-font-w-600 text-truncate maxw-200px'>
                            {t(child.translation)}
                        </p>
                    </CustomTd>

                    {columns.map((column) => {
                        const permission = resolveCellPermission(childPermissions, column);

                        return (
                            <CustomTd tableId={tableId} key={`${child.id}-${column}`}>
                                <div className='d-flex justify-content-center align-items-center'>
                                    <CustomCheckbox
                                        disabled={readOnly}
                                        icon={
                                            !permission ? (
                                                <IconCheckbox />
                                            ) : (
                                                <IconCheckboxUnchecked className='icon-color-general-950' />
                                            )
                                        }
                                        checkedIcon={
                                            !permission ? (
                                                <IconCheckbox />
                                            ) : (
                                                <IconCheckboxChecked className='icon-color-shades-800' />
                                            )
                                        }
                                        checked={selectedPermissions.some((item) => item.id === permission?.id)}
                                        onChange={(e) => handleCheckboxChange(permission?.id, e.target.checked)}
                                    />
                                </div>
                            </CustomTd>
                        );
                    })}
                </>
            );
        }),

        data: selectedRole?.children?.length ? selectedRole.children : [selectedRole],
    };

    /**
     * Permissions of one tab the role has selected, painted on its badge and on
     * the card of the module.
     *
     * @param {Object} role - Entry of MOBILE_ROLES_MODEL.
     * @returns {number} Selected permissions of that tab.
     */
    const getSelectedCount = (role) => countSelectedPermissions(getRolePermissions(role), selectedPermissions);

    const selectedRoleCount = getSelectedCount(selectedRole);

    // Only the permissions the table renders as a checkbox can be toggled, so the select all
    // checkbox is computed over those and not over every permission of the module
    const currentPermissions = [
        ...new Map(
            (selectedRole?.children?.length ? selectedRole.children : [selectedRole])
                .flatMap((child) => {
                    const childPermissions = permissions.filter(
                        (permission) => permission.moduleId === child?.id,
                    );

                    return columns.map((column) => resolveCellPermission(childPermissions, column));
                })
                .filter((permission) => permission?.id)
                .map((permission) => [permission.id, permission]),
        ).values(),
    ];

    const isAllChecked = isAllPermissionsSelected(currentPermissions, selectedPermissions);

    const handleSelectAll = (checked) => {
        handleSelectAllPermissions(checked, currentPermissions, setSelectedPermissions);
    };

    return {
        t,
        roles,
        selectedInnerView,
        handleStepSelectedInnerView,
        selectedRole,
        thead,
        tbody,
        selectedRoleCount,
        getSelectedCount,
        isAllChecked,
        handleSelectAll,
    };
};

export default MobileRolesApp;
